import { useState } from 'react';
import { FieldExtensionComponentProps } from "@backstage/plugin-scaffolder-react";
import {
	fetchApiRef,
	useApi,
  } from '@backstage/core-plugin-api';
  import {
	Progress,
	ErrorPanel,
  } from '@backstage/core-components';
import { useAsync } from 'react-use';
import { FormControl, FormHelperText, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { sortBy } from 'lodash';
import { z } from 'zod';
import { AzureDevOpsOauthWrapper } from '../AzureDevOpsOauthWrapper';
import { useAzureDevOpsOauthContext } from '../../hooks/useOauthContext';
import { azureDevOpsFetch } from '../helpers';

const uiOptionsSchema = z.object({
	organization: z.string().describe('The Azure DevOps organization to query'),
	title: z.string().optional().describe('The title of the field'),
	tokenSecretName: z.string().optional().describe('The name of the secret to store the token in'),
	extraScopes: z.array(z.string()).optional().describe('Additional OAuth scopes to request'),
});

export const AzureDevOpsProjectFieldSchema = makeFieldSchema({
	output: x => x.string(),
	uiOptions: zImpl => zImpl.object({
		organization: zImpl.string().describe('The Azure DevOps organization to query'),
		title: zImpl.string().optional().describe('The title of the field'),
		tokenSecretName: zImpl.string().optional().describe('The name of the secret to store the token in'),
		extraScopes: zImpl.array(zImpl.string()).optional().describe('Additional OAuth scopes to request'),
	}),
});

export const AzureDevOpsProjectFieldScopes = ['https://app.vssps.visualstudio.com/vso.project'];

export const AzureDevOpsProjectFieldComponent = (
	props: FieldExtensionComponentProps<string, { organization: string } & {
		onProjectChange?: (value: string) => void;
	}>,
) => {
	const fetchApi = useApi(fetchApiRef);
	const { token } = useAzureDevOpsOauthContext();
	const [projects, setProjects] = useState<{ label: string, value: string }[]>([]);
	const { error } = useAsync(async () => {		
		const organization = props.uiSchema['ui:options']?.organization;
		const body = await azureDevOpsFetch({
			url: `https://dev.azure.com/${organization}/_apis/projects?api-version=7.2-preview.4`,
			token,
			fetchApi,
		});
		const fetchedProjects = body.value.map((project: any) => ({
			label: project.name,
			value: project.name,
		}));
		setProjects(sortBy(fetchedProjects, 'label'));
	});

	if (error) {
		return <ErrorPanel error={error} />;
	}
	if (!projects) {
		return <Progress />;
	}

	return (
		<FormControl
			margin="normal"
			required={props.required}
			error={(props?.rawErrors?.length || 0) > 0 && !props.formData}
		>
			<Autocomplete
				options={projects}
				freeSolo={false}
				renderInput={(params) => <TextField {...params} label="Azure DevOps Project" variant="outlined" />}
				getOptionLabel={(option) => option.label}
				onChange={(_, value) => {
					props.onProjectChange?.(value?.value);
					props.onChange(value?.value);
				}}
			/>
			<FormHelperText>Select a project from the list.</FormHelperText>
		</FormControl>
	)
};

AzureDevOpsProjectFieldComponent.defaultProps = {
	onProjectChange: (_: string) => {},
};

export const AzureDevOpsProjectField = (props: FieldExtensionComponentProps<string, {
	organization: string,
	tokenSecretName?: string,
	extraScopes? : string[],
}>) => {
	if (!props.uiSchema) {
		return <ErrorPanel error={new Error("No uiSchema provided")} />;
	}
	
	const result = uiOptionsSchema.safeParse(props.uiSchema['ui:options']);
	const scopes = Array.from(new Set(AzureDevOpsProjectFieldScopes.concat(result.data?.extraScopes ?? [])));
	const tokenSecretName = props.uiSchema['ui:options']?.tokenSecretName;
	
	if (result.success) {
		return (
			<AzureDevOpsOauthWrapper {...props} scopes={scopes} tokenSecretName={tokenSecretName} >
				<AzureDevOpsProjectFieldComponent {...props} />
			</AzureDevOpsOauthWrapper>
		)
	}

	return <ErrorPanel error={result.error} />;
};
